import SHA256 from "crypto-js/sha256";
import { QUADRANT_RANGE } from "src/constants";
import { superNumerary } from "src/constants/constant";
import { KID_TOOTH_NUMBERS } from "src/components/new-design/canvas/canvas-constant";
import { ClaimLineItemObject } from "src/components/new-design/canvas/types";
import { ServiceLineObject } from "src/context/canvas/types";
import { userService } from "src/services";

const PHI_HASH_PREFIX = "****";
const CDT_MIN = 0;
const CDT_MAX = 9999;

type PhiField = "procedure" | "claimTooth" | "quadrant";

/** hash → plaintext. Empty string means two candidates collided. */
const reverseHashMaps: Record<PhiField, Map<string, string> | null> = {
  procedure: null,
  claimTooth: null,
  quadrant: null,
};

/** Config objects used to build the procedure map. A new profile replaces them. */
let procedureDictionarySource: {
  predictionConfig: object | null;
  annotationRules: object | null;
} | null = null;

/** The getters return a fresh {} / [] when unset, which would defeat the reference check. */
function nonEmpty<T extends object>(value: T | null | undefined): T | null {
  return value && Object.keys(value).length ? value : null;
}

/** Matches query-api phifilter.HashPHIString (unsalted SHA-256, first 8 hex chars). */
export function hashPhiString(value: string): string {
  if (!value) {
    return "";
  }
  const hex = SHA256(value).toString();
  return `${PHI_HASH_PREFIX}${hex.slice(0, 8)}`;
}

export function isPhiHashed(value?: string | null): value is string {
  return typeof value === "string" && value.startsWith(PHI_HASH_PREFIX);
}

function toothCandidates(): string[] {
  const candidates: string[] = [];
  for (let tooth = 1; tooth <= 32; tooth += 1) {
    candidates.push(String(tooth));
  }
  KID_TOOTH_NUMBERS.forEach((tooth) => candidates.push(tooth));
  superNumerary.forEach((tooth) => candidates.push(tooth));
  return candidates;
}

function quadrantCandidates(): string[] {
  return Object.keys(QUADRANT_RANGE);
}

function addProcedureCandidate(seen: Set<string>, candidate?: string | null) {
  if (!candidate || seen.has(candidate)) {
    return;
  }
  seen.add(candidate);
}

/** Expand a config band to D + 4-digit codes. Bands outside 0000–9999 are ignored. */
function addProcedureRange(
  seen: Set<string>,
  minRaw: number | string,
  maxRaw: number | string
) {
  const min = Number(minRaw);
  const max = Number(maxRaw);
  if (!Number.isInteger(min) || !Number.isInteger(max)) {
    return;
  }
  if (min < CDT_MIN || max > CDT_MAX || min > max) {
    return;
  }
  for (let n = min; n <= max; n += 1) {
    addProcedureCandidate(seen, `D${n.toString().padStart(4, "0")}`);
  }
}

function procedureCandidates(): string[] {
  const seen = new Set<string>();

  const predictionConfig = userService.getPredictionConfig() || {};
  Object.keys(predictionConfig).forEach((procedure) => {
    addProcedureCandidate(seen, procedure);
  });

  const rules = userService.getAnnotationDisplayRules() || [];
  rules.forEach((rule) => {
    rule?.listOfProcedures?.forEach((procedure) => {
      addProcedureCandidate(seen, procedure);
    });
    rule?.rangeOfProcedures?.forEach((range) => {
      addProcedureRange(seen, range.min, range.max);
    });
  });

  return Array.from(seen);
}

function candidatesForField(field: PhiField): string[] {
  if (field === "procedure") {
    return procedureCandidates();
  }
  if (field === "claimTooth") {
    return toothCandidates();
  }
  return quadrantCandidates();
}

function getReverseHashMap(field: PhiField): Map<string, string> {
  if (field === "procedure") {
    const predictionConfig = nonEmpty(userService.getPredictionConfig());
    const annotationRules = nonEmpty(userService.getAnnotationDisplayRules());
    const sourceChanged =
      !procedureDictionarySource ||
      procedureDictionarySource.predictionConfig !== predictionConfig ||
      procedureDictionarySource.annotationRules !== annotationRules;
    if (sourceChanged) {
      reverseHashMaps.procedure = null;
      procedureDictionarySource = { predictionConfig, annotationRules };
    }
  }

  const existing = reverseHashMaps[field];
  if (existing) {
    return existing;
  }

  const map = new Map<string, string>();
  candidatesForField(field).forEach((candidate) => {
    const hashed = hashPhiString(candidate);
    const prior = map.get(hashed);
    if (prior === undefined) {
      map.set(hashed, candidate);
      return;
    }
    if (prior !== candidate) {
      map.set(hashed, "");
    }
  });

  reverseHashMaps[field] = map;
  return map;
}

function resolveHashedField(
  hashed: string,
  field: PhiField
): string | undefined {
  if (!isPhiHashed(hashed)) {
    return hashed;
  }

  const resolved = getReverseHashMap(field).get(hashed);
  return resolved || undefined;
}

/** Pre-resolve PHI tokens for all service lines on the current claim. */
export function warmPhiCodebookFromServiceLines(
  serviceLines: ServiceLineObject[] = []
) {
  serviceLines.forEach((line) => {
    if (isPhiHashed(line.procedure)) {
      resolveHashedField(line.procedure, "procedure");
    }
    if (isPhiHashed(line.quadrant)) {
      resolveHashedField(line.quadrant, "quadrant");
    }
    const tooth = line.toothNumber ?? line.claimTooth;
    if (isPhiHashed(tooth)) {
      resolveHashedField(tooth, "claimTooth");
    }
  });
}

export function recoverClaimLineItem(
  claimLineItem: ClaimLineItemObject
): ClaimLineItemObject {
  const item = { ...claimLineItem };

  (["procedure", "claimTooth", "quadrant"] as const).forEach((field) => {
    const value = claimLineItem[field];
    if (value === undefined || value === null || value === "") {
      return;
    }
    if (!isPhiHashed(value)) {
      return;
    }
    const resolved = resolveHashedField(value, field);
    if (resolved) {
      item[field] = resolved;
    }
  });

  return item;
}

/** Display text only. Leaves polygon.key intact for visibility lookups. */
export function maskPhiFindingKey(key: string): string {
  if (!key) {
    return key;
  }
  const toothFinding = key.match(/^Tooth #(\S+)(.*)$/);
  if (toothFinding) {
    return `Tooth #${hashPhiString(toothFinding[1])}${toothFinding[2]}`;
  }
  const bridge = key.match(/^(\S+) (Pontic|Retainer|Implant|Tooth)$/);
  if (bridge) {
    return `${hashPhiString(bridge[1])} ${bridge[2]}`;
  }
  return key;
}

export function resetPhiCodebookForTests() {
  reverseHashMaps.procedure = null;
  reverseHashMaps.claimTooth = null;
  reverseHashMaps.quadrant = null;
  procedureDictionarySource = null;
}
